import type { Quaternion } from '../types';
import {
  conjugateQuaternion,
  multiplyQuaternions,
  normalizeQuaternion,
  quaternionFromAxisAngle,
  type Vector3,
} from './quaternion';

/** The virtual ball a drag turns, in the same screen pixels as the pointer. */
export type TrackballSphere = { x: number; y: number; radius: number };

type ScreenPoint = { x: number; y: number };

/**
 * A screen point lifted onto the ball, in camera space: x right, y up, z towards the eye.
 *
 * Bell's trackball. Inside the ball's silhouette the point lands on the sphere; outside it lands
 * on a hyperbolic sheet that meets the sphere smoothly at 1/√2 of the radius, so a drag that leaves
 * the ball keeps turning it rather than stopping dead at the rim.
 */
export const toTrackballPoint = (
  { x: centerX, y: centerY, radius }: TrackballSphere,
  { x, y }: ScreenPoint
): Vector3 => {
  const across = (x - centerX) / radius;
  const up = (centerY - y) / radius;
  const squared = across * across + up * up;
  const toward =
    squared <= 0.5 ? Math.sqrt(1 - squared) : 0.5 / Math.sqrt(squared);
  const length = Math.hypot(across, up, toward);
  return [across / length, up / length, toward / length];
};

/** The turn that carries the ball's point under `from` to the point under `to`, in camera space. */
export const toTrackballTurn = (
  sphere: TrackballSphere,
  from: ScreenPoint,
  to: ScreenPoint
): Quaternion => {
  const [ax, ay, az] = toTrackballPoint(sphere, from);
  const [bx, by, bz] = toTrackballPoint(sphere, to);
  const axis: Vector3 = [
    ay * bz - az * by,
    az * bx - ax * bz,
    ax * by - ay * bx,
  ];
  const angle = Math.acos(
    Math.max(-1, Math.min(1, ax * bx + ay * by + az * bz))
  );
  return quaternionFromAxisAngle(axis, angle);
};

/**
 * The orientation after dragging the ball from `from` to `to`.
 *
 * The drag turns the scene, so the camera turns the opposite way about the same axis: the scene's
 * turn is in camera space, so it is applied on the camera side of the orientation and inverted.
 */
export const turnTrackball = (
  orientation: Quaternion,
  sphere: TrackballSphere,
  from: ScreenPoint,
  to: ScreenPoint
): Quaternion =>
  normalizeQuaternion(
    multiplyQuaternions(
      orientation,
      conjugateQuaternion(toTrackballTurn(sphere, from, to))
    )
  );

/** The ball inscribed in a canvas of this size, centered on it. */
export const toCanvasTrackball = ({
  width,
  height,
}: {
  width: number;
  height: number;
}): TrackballSphere => ({
  x: width / 2,
  y: height / 2,
  radius: Math.min(width, height) / 2,
});
